/**
 * EVENTSFLO-SECURITY-HEADERS-AND-BASELINE-POSTURE-REVIEW-V1
 *
 * SuperAdmin-only READ-ONLY security baseline diagnostic card.
 *
 * Safety:
 *   - Hidden for non-SuperAdmin (returns null).
 *   - Calls a read-only diagnostic endpoint; never mutates.
 *   - Does NOT show any secret / API key / Mongo URL / token.
 *   - No "one-click HSTS enable" or "one-click CSP enforce"
 *     controls — header configuration is env-driven only.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  AlertTriangle,
  CheckCircle2,
  Lock,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";

const ENDPOINT = "/api/admin/security/baseline/status";

async function authedFetch(url, token) {
  const r = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}${url}`,
    { method: "GET", headers: { Authorization: `Bearer ${token}` } },
  );
  if (!r.ok) {
    let detail = `${r.status} ${r.statusText}`;
    try {
      const body = await r.json();
      if (body?.detail) detail = body.detail;
    } catch { /* ignore */ }
    throw new Error(detail);
  }
  return r.json();
}

export default function SecurityBaselineCard() {
  const { user, token } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setLoading(true);
    try {
      const body = await authedFetch(ENDPOINT, token);
      setData(body);
    } catch (e) {
      toast.error(e.message || "Security baseline fetch failed");
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, token]);

  // EVENTSFLO-SECURITY-CSP-REPORT-ONLY-STAGING-V1
  // Read-only CSP reports viewer + observed-header drift check.
  const [reports, setReports] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [drift, setDrift] = useState(null);
  const [driftLoading, setDriftLoading] = useState(false);

  const loadReports = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setReportsLoading(true);
    try {
      const body = await authedFetch(
        "/api/admin/security/csp-reports?limit=20&since_hours=24",
        token,
      );
      setReports(body);
    } catch (e) {
      toast.error(e.message || "CSP reports fetch failed");
    } finally {
      setReportsLoading(false);
    }
  }, [isSuperAdmin, token]);

  const checkObservedHeaders = useCallback(async () => {
    if (!isSuperAdmin || !data) return;
    setDriftLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/`;
      const r = await fetch(url, { method: "GET", credentials: "omit" });
      const observed = {};
      r.headers.forEach((v, k) => {
        observed[k.toLowerCase()] = v;
      });
      const expectedPairs = data.expected_headers || [];
      const missing = [];
      const mismatched = [];
      const ok = [];
      for (const [name, expectedValue] of expectedPairs) {
        const lk = name.toLowerCase();
        if (!(lk in observed)) {
          missing.push(name);
        } else if (observed[lk] !== expectedValue) {
          mismatched.push({
            name,
            expected: expectedValue,
            observed: observed[lk],
          });
        } else {
          ok.push(name);
        }
      }
      setDrift({
        checked_url: url,
        observed_header_count: Object.keys(observed).length,
        ok,
        missing,
        mismatched,
        proxy_drift_possible: missing.length > 0 || mismatched.length > 0,
      });
    } catch (e) {
      toast.error(e.message || "Observed-headers check failed");
    } finally {
      setDriftLoading(false);
    }
  }, [isSuperAdmin, data]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadReports(); }, [loadReports]);

  const formatWhen = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  if (!isSuperAdmin) return null;

  return (
    <Card
      className="border-zinc-700/40 bg-zinc-900/40"
      data-testid="security-baseline-card"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-zinc-200 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> Security baseline status
          {data ? (
            <Badge
              data-testid="security-baseline-overall-status"
              className={
                data.production_safe
                  ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                  : "bg-amber-500/15 text-amber-300 border border-amber-500/30"
              }
            >
              {data.production_safe ? "production-safe" : "baseline-only"}
            </Badge>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-end mb-2">
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="security-baseline-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
        <div
          className="rounded-md border border-zinc-500/30 bg-zinc-500/5 p-2 text-xs text-zinc-300 flex items-start gap-2 mb-3"
          data-testid="security-baseline-readonly-note"
        >
          <Lock className="w-3.5 h-3.5 mt-0.5" />
          <span>
            Read-only diagnostic. Header configuration is env-driven
            (EVENTSFLO_SECURITY_HEADERS_ENABLED / EVENTSFLO_HSTS_ENABLED /
            EVENTSFLO_CSP_MODE). No changes can be made from this panel.
          </span>
        </div>
        {data ? (
          <>
            <div
              className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs"
              data-testid="security-baseline-grid"
            >
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">Headers enabled</div>
                <div
                  className="text-zinc-100 text-base font-semibold"
                  data-testid="security-baseline-headers-enabled"
                >
                  {data.security_headers_enabled ? "yes" : "no"}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">HSTS</div>
                <div
                  className="text-zinc-100 text-base font-semibold"
                  data-testid="security-baseline-hsts"
                >
                  {data.hsts_enabled ? "on" : "off"}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">CSP mode</div>
                <div
                  className="text-zinc-100 text-base font-semibold"
                  data-testid="security-baseline-csp-mode"
                >
                  {data.csp_mode}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">CORS</div>
                <div
                  className="text-zinc-100 text-xs"
                  data-testid="security-baseline-cors"
                >
                  {data.cors_summary.wildcard_origin
                    ? "wildcard"
                    : `${data.cors_summary.origin_count} origin(s)`}
                  {data.cors_summary.allow_credentials ? " · creds" : ""}
                </div>
              </div>
            </div>
            <div className="mt-3">
              <div className="text-xs text-zinc-400 mb-1">
                Headers configured ({data.headers_configured.length})
              </div>
              <div className="flex flex-wrap gap-1">
                {data.headers_configured.map((h) => (
                  <Badge
                    key={h}
                    data-testid={`security-baseline-header-${h.toLowerCase()}`}
                    className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-mono text-[10px]"
                  >
                    <CheckCircle2 className="w-3 h-3 mr-1" /> {h}
                  </Badge>
                ))}
              </div>
            </div>
            {data.missing_recommended_headers.length > 0 ? (
              <div className="mt-3">
                <div className="text-xs text-zinc-400 mb-1">
                  Missing recommended ({data.missing_recommended_headers.length})
                </div>
                <div className="flex flex-wrap gap-1">
                  {data.missing_recommended_headers.map((h) => (
                    <Badge
                      key={h}
                      data-testid={`security-baseline-missing-${h.toLowerCase()}`}
                      className="bg-rose-500/15 text-rose-300 border border-rose-500/30 font-mono text-[10px]"
                    >
                      <AlertTriangle className="w-3 h-3 mr-1" /> {h}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}
            {data.warnings.length > 0 ? (
              <div className="mt-3">
                <div className="text-xs text-zinc-400 mb-1">
                  Warnings ({data.warnings.length})
                </div>
                <ul
                  className="text-xs text-amber-300 space-y-1"
                  data-testid="security-baseline-warnings"
                >
                  {data.warnings.map((w, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-1"
                      data-testid={`security-baseline-warning-${idx}`}
                    >
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : (
          <div
            className="text-xs text-zinc-500"
            data-testid="security-baseline-empty"
          >
            {loading
              ? "Loading security baseline…"
              : "No security baseline data available."}
          </div>
        )}

        {/* EVENTSFLO-SECURITY-CSP-REPORT-ONLY-STAGING-V1
            CSP staging status + reports viewer + drift check.
            Read-only. No enforce / clear / delete controls. */}
        {data ? (
          <div
            className="mt-4 rounded-md border border-zinc-700/40 p-3"
            data-testid="security-csp-staging-section"
          >
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2 mb-2">
              <ShieldCheck className="w-3.5 h-3.5" /> CSP staging status
            </div>
            <div
              className="rounded-md border border-zinc-500/30 bg-zinc-500/5 p-2 text-xs text-zinc-300 flex items-start gap-2 mb-2"
              data-testid="security-csp-staging-note"
            >
              <Lock className="w-3.5 h-3.5 mt-0.5" />
              <span>
                Report-only mode records violations but does not
                block resources. Enforced CSP remains a later phase.
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">CSP mode</div>
                <div
                  className="text-zinc-100 text-base font-semibold"
                  data-testid="security-csp-staging-mode"
                >
                  {data.csp_mode}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">Collector</div>
                <div
                  className="text-zinc-100 text-base font-semibold"
                  data-testid="security-csp-staging-collector"
                >
                  {data.csp_report_collector_enabled ? "on" : "off"}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">Report endpoint</div>
                <div
                  className="text-zinc-100 text-xs font-mono"
                  data-testid="security-csp-staging-endpoint"
                >
                  {data.csp_report_endpoint}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">Enforced CSP</div>
                <div
                  className="text-zinc-100 text-base font-semibold"
                  data-testid="security-csp-staging-enforced"
                >
                  {data.csp_mode === "enforce" ? "yes" : "no"}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* CSP reports viewer */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="security-csp-reports-section"
        >
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" /> Recent CSP reports
            </div>
            <Button
              variant="outline"
              onClick={loadReports}
              disabled={reportsLoading}
              data-testid="security-csp-reports-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
          {reports && Array.isArray(reports.rows) && reports.rows.length > 0 ? (
            <div className="overflow-auto rounded border border-zinc-700/40 max-h-64">
              <table
                className="w-full text-xs"
                data-testid="security-csp-reports-table"
              >
                <thead className="bg-zinc-800/60 text-zinc-400 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">When</th>
                    <th className="text-left px-2 py-1">Directive</th>
                    <th className="text-left px-2 py-1">Disposition</th>
                    <th className="text-left px-2 py-1">Document</th>
                    <th className="text-left px-2 py-1">Blocked</th>
                    <th className="text-left px-2 py-1">Source</th>
                    <th className="text-left px-2 py-1">Line:Col</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.rows.map((row, idx) => (
                    <tr
                      key={row.id || `csp-${idx}`}
                      className="odd:bg-zinc-900/30"
                      data-testid={`security-csp-reports-row-${idx}`}
                    >
                      <td className="px-2 py-1 text-zinc-300 whitespace-nowrap">
                        {formatWhen(row.created_at)}
                      </td>
                      <td className="px-2 py-1 font-mono text-zinc-300">
                        {row.effective_directive || row.violated_directive || "—"}
                      </td>
                      <td className="px-2 py-1">
                        <Badge
                          className={
                            row.disposition === "enforce"
                              ? "bg-rose-500/15 text-rose-300 border border-rose-500/30"
                              : "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                          }
                        >
                          {row.disposition || "report"}
                        </Badge>
                      </td>
                      <td className="px-2 py-1 text-zinc-300 font-mono break-all">
                        {row.document_uri_safe || "—"}
                      </td>
                      <td className="px-2 py-1 text-zinc-300 font-mono break-all">
                        {row.blocked_uri_safe || "—"}
                      </td>
                      <td className="px-2 py-1 text-zinc-300 font-mono break-all">
                        {row.source_file_safe || "—"}
                      </td>
                      <td className="px-2 py-1 text-zinc-300 font-mono">
                        {row.line_number ?? "—"}:{row.column_number ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              className="text-xs text-zinc-500"
              data-testid="security-csp-reports-empty"
            >
              {reportsLoading
                ? "Loading recent CSP reports…"
                : "No recent CSP violation reports recorded."}
            </div>
          )}
          {reports ? (
            <div className="text-xs text-zinc-500 mt-2">
              showing {reports.returned_count} of {reports.total_recent}
              {" · "}since last {reports.since_hours}h
              {" · "}sanitised URIs only · no raw report bodies stored
            </div>
          ) : null}
        </div>

        {/* Observed-header drift check */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="security-observed-headers-section"
        >
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" /> Observed header
              drift check
            </div>
            <Button
              variant="outline"
              onClick={checkObservedHeaders}
              disabled={driftLoading || !data}
              data-testid="security-observed-headers-check-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Check
              observed headers
            </Button>
          </div>
          <div className="text-xs text-zinc-400 mb-2">
            Compares headers expected by the server with what the
            browser actually receives via the proxy. Read-only.
          </div>
          {drift ? (
            <div
              className="text-xs text-zinc-300 space-y-2"
              data-testid="security-observed-headers-result"
            >
              <div>
                Checked: <span className="font-mono">{drift.checked_url}</span>
                {" · "}{drift.observed_header_count} headers observed
              </div>
              <div className="flex flex-wrap gap-1">
                {drift.ok.map((h) => (
                  <Badge
                    key={`ok-${h}`}
                    className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-mono text-[10px]"
                    data-testid={`security-observed-ok-${h.toLowerCase()}`}
                  >
                    <CheckCircle2 className="w-3 h-3 mr-1" /> {h}
                  </Badge>
                ))}
                {drift.missing.map((h) => (
                  <Badge
                    key={`miss-${h}`}
                    className="bg-rose-500/15 text-rose-300 border border-rose-500/30 font-mono text-[10px]"
                    data-testid={`security-observed-missing-${h.toLowerCase()}`}
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" /> missing {h}
                  </Badge>
                ))}
                {drift.mismatched.map((m) => (
                  <Badge
                    key={`mm-${m.name}`}
                    className="bg-amber-500/15 text-amber-300 border border-amber-500/30 font-mono text-[10px]"
                    data-testid={`security-observed-mismatch-${m.name.toLowerCase()}`}
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" /> mismatch {m.name}
                  </Badge>
                ))}
              </div>
              {drift.proxy_drift_possible ? (
                <div className="text-amber-300 text-xs">
                  Proxy drift possible — proxies may strip or modify
                  security headers in transit. Verify with browser
                  devtools.
                </div>
              ) : (
                <div className="text-emerald-300 text-xs">
                  All expected baseline headers were observed
                  end-to-end.
                </div>
              )}
            </div>
          ) : (
            <div
              className="text-xs text-zinc-500"
              data-testid="security-observed-headers-empty"
            >
              Click <em>Check observed headers</em> to compare
              expected vs browser-observed.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
